import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { updateOptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item, Option, OptionBinding as OptionBindingT } from 'apis/restaurant-api/merchant-admin/menu2/types';
import useMenuData from 'modules/menu/useMenuData';

import OptionBindingForm from './OptionBindingForm';
import OptionBindingHeader from './OptionBindingHeader';
import OptionBindingView from './OptionBindingView';
import { OptionBindingFormValues } from './types';
import { optionBindingFormValuesToUpdatePayload } from './utils';
import { MenuLocaleContext } from '../../../MenuLocaleContext';

type Props = {
  disableHoverStyle: boolean;
  dragging: boolean;
  item: Item;
  onToggleOpen: () => void;
  open: boolean;
  optionBinding: OptionBindingT;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Root = styled.div``;

const OpenContent = styled.div`
  animation: ${fadeIn} 0.15s ease-in 1;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const OptionBinding = (
  { disableHoverStyle, dragging, item, onToggleOpen, open, optionBinding }: Props,
  ref: React.RefObject<HTMLDivElement>,
) => {
  const intl = useIntl();
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const { options, updateMenuData } = useMenuData();

  const [editing, setEditing] = useState(false);

  const handleStartEdit = useCallback(() => {
    setEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  const option = options.find(option => option.id === optionBinding.option_id);

  if (!option) {
    throw new Error(`Option \`${optionBinding.option_id}\` not found in global state`);
  }

  const otherItemOptionIds = item.option_bindings.filter(ob => ob.id !== optionBinding.id).map(ob => ob.option_id);
  const otherItemOptions = otherItemOptionIds
    .map(optionId => options.find(option => option.id === optionId))
    .filter((otherOption): otherOption is Option => otherOption !== undefined)
    .filter(otherOption => otherOption.id !== option.id);

  const { mutateAsync: handleFormSubmit } = useMutation(
    async (formValues: OptionBindingFormValues) => {
      const updatePayload = optionBindingFormValuesToUpdatePayload(primaryLanguage, optionBinding, formValues);
      return updateOptionBinding(item.menu_id, item.id, item.version, optionBinding.id, updatePayload);
    },
    {
      onSuccess: updatedItem => {
        updateMenuData({ actionType: 'updateItem', payload: updatedItem });
        setEditing(false);
        onToggleOpen();
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'menu.optionBindingUpdateFailed' }));
      },
    },
  );

  return (
    <Root ref={ref}>
      <OptionBindingHeader
        disableHoverStyle={disableHoverStyle}
        hidden={dragging}
        itemId={item.id}
        onToggleOpen={onToggleOpen}
        option={option}
        optionBindingId={optionBinding.id}
      />

      {open && (
        <OpenContent>
          {editing ? (
            <OptionBindingForm
              onCancelEdit={handleCancelEdit}
              onSubmit={handleFormSubmit}
              option={option}
              optionBinding={optionBinding}
              otherItemOptions={otherItemOptions}
            />
          ) : (
            <OptionBindingView
              onStartEdit={handleStartEdit}
              option={option}
              optionBinding={optionBinding}
              otherItemOptions={otherItemOptions}
            />
          )}
        </OpenContent>
      )}
    </Root>
  );
};

export default React.forwardRef<HTMLDivElement, Props>(OptionBinding);
